import { RecommendationData } from './useRecommendation';
import React from 'react';
import moment from 'moment';
import { STANDARD_DATE } from '../../../../utilities/dateHelper';
import ButtonComponent from '../../../../components/button/buttonComponent';
import CustomTooltip from '../../../../components/CustomTooltip';
import { twMerge } from 'tailwind-merge';
import testKitImg from '../../../../assets/images/test-kit-images/img.svg';
import { ServiceRequestStatus } from '../../../../graphql/generated/hasura';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';

interface IRecommendationItemMobile {
  recommendation: RecommendationData;
  containerClassNames?: string;
  showImage?: boolean;
  locale: Record<string, string>;
  handleNameClick: () => void;
  moveToArchive?: () => void;
  deleteRecommendation?: () => void;
  purchaseRecommendation: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const RecommendationItemMobile: React.FC<IRecommendationItemMobile> = ({
  recommendation,
  containerClassNames = 'rounded-10 bg-white w-full border border-graph-border-color py-4 px-5',
  locale,
  handleNameClick,
  moveToArchive,
  showImage,
  deleteRecommendation,
  purchaseRecommendation,
}) => {
  return (
    <div className="desktop:hidden">
      <div className={`w-full ${containerClassNames}`}>
        <div className="flex flex-col gap-2.5 items-start">
          <div className="flex flex-col w-full gap-1 items-start">
            <div className="flex w-full gap-2.5 items-center justify-between">
              {showImage && (
                <div className="overflow-hidden rounded-10 w-[42px] h-[42px] bg-cover bg-center">
                  <img
                    src={testKitImg}
                    className="object-cover w-full h-full"
                    alt={`${recommendation.productTitle}-image`}
                  />
                </div>
              )}
              {(recommendation.showArchive ||
                recommendation.status === ServiceRequestStatus.Active) &&
                moveToArchive && (
                  <ButtonComponent
                    testID="move-to-archive-button"
                    onClick={moveToArchive}
                    className={twMerge(
                      'group w-fit bg-transparent px-1.5 -ml-2.5',
                      recommendation.hasInCart && 'hidden',
                    )}
                    type="icon"
                    Icon={() => (
                      <Trash className="w-5 h-5 min-w-[20px] fill-med-gray-3 group-hover:fill-white " />
                    )}
                  />
                )}

              {(recommendation.hasInCart ||
                recommendation.status === ServiceRequestStatus.OnHold) &&
                deleteRecommendation && (
                  <ButtonComponent
                    testID="move-to-archive-button"
                    onClick={deleteRecommendation}
                    className={twMerge(
                      'group w-fit bg-transparent px-1.5 -ml-2.5',
                    )}
                    type="icon"
                    Icon={() => (
                      <Trash className="w-5 h-5 min-w-[20px] fill-med-gray-3 group-hover:fill-white " />
                    )}
                  />
                )}
            </div>
            <div className="text-base text-btn-black font-semibold">
              {recommendation.productTitle}
            </div>
            <div className="text-sm font-medium text-med-gray-3">
              {locale.quantity} {recommendation.quantity}{' '}
              {Number(recommendation.quantity) > 1 ? locale.tests : locale.test}
            </div>

            <div className="text-xs text-med-gray-3">
              {moment(recommendation.createdAt).format(STANDARD_DATE)}
            </div>

            <div className="flex items-center text-xs text-med-gray-3">
              <div>{locale.provider}</div>

              <ButtonComponent
                type="underline"
                onClick={handleNameClick}
                noUpperCase
                testID={'provider-name'}
                className="ml-1 !text-clc-blue !text-xs whitespace-pre-line"
              >
                {recommendation.providerName}
              </ButtonComponent>
            </div>
          </div>

          <div className="flex flex-col gap-2.5 items-start w-full">
            <div className="flex justify-between items-center w-full">
              <div
                className={twMerge(
                  'hidden text-sm font-semibold w-fit text-bright-green border rounded-full border-bright-green bg-green-50 px-2.5',
                  recommendation.hasInCart && 'block',
                )}
              >
                Added to cart
              </div>

              <div className="text-base text-btn-black font-semibold">
                {locale.total}: ${(recommendation.totalPrice / 100).toFixed(2)}
              </div>

              {recommendation.isPurchased && (
                <span className="capitalize inline-block text-sm font-semibold border px-3 min-w-[6rem] text-center rounded-full text-bright-green border-bright-green bg-green-50">
                  {locale.purchased}
                </span>
              )}
            </div>

            <ButtonComponent
              testID="purchase-button"
              dataTooltipId={
                recommendation.hasInCart ? recommendation.id : undefined
              }
              disabled={recommendation.hasInCart}
              onClick={purchaseRecommendation}
              fullWidthClassName="w-full"
              className={twMerge(recommendation.hasInCart && 'hidden')}
              containerClassName="text-sm"
            >
              Add to cart
            </ButtonComponent>
            <CustomTooltip place="top" id={recommendation.id}>
              In cart right now
            </CustomTooltip>
          </div>
        </div>
      </div>
    </div>
  );
};
